/**
 * adsSchedule.ts — the weekly automatic ad run: its saved settings, the fallback defaults, and the pure "is it time?" rule.
 * Off until a manager turns it on. Only LinkedIn ads are written automatically; Google needs an ad group and keyword a person has chosen.
 */
import { z } from "zod";
import { weekStartOf } from "./ads.ts";
import { DEFAULT_INSIGHT_SETTINGS, insightSettingsSchema } from "./adsInsights.ts";

export const WEEKDAY_LABEL = ["", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;

/** Used for any setting nobody has saved. */
export const AD_GOALS = ["demo_bookings", "free_trials", "early_access", "website_visits"] as const;
export const adGoalSchema = z.enum(AD_GOALS);
export type AdGoal = (typeof AD_GOALS)[number];
/** What each goal is called on screen, and the sign-up each ad is counted by. */
export const AD_GOAL_LABEL: Record<AdGoal, string> = { demo_bookings: "Demo bookings", free_trials: "Free trials", early_access: "Early-access sign-ups", website_visits: "Website visits" };

export const AD_SCHEDULE_DEFAULTS = {
  enabled: false, weekday: 1, hour: 11, minute: 0, sonicHire: true, raya: true, linkedinCount: 3, monthlyCapUsd: 20, autoPick: false, autoPickMinScore: 4,
  goalSonicHire: "demo_bookings", goalRaya: "early_access", dailyCapUsd: 35, targetCpaUsd: null, insightSettings: DEFAULT_INSIGHT_SETTINGS,
} as const;

export const adScheduleSchema = z.object({
  enabled: z.boolean(),
  weekday: z.number().int().min(1).max(7),
  hour: z.number().int().min(0).max(23),
  minute: z.number().int().min(0).max(59),
  sonicHire: z.boolean(),
  raya: z.boolean(),
  linkedinCount: z.number().int().min(1).max(5),
  monthlyCapUsd: z.number().min(1).max(1000),
  /** The AI writes a couple more ads than needed and the best ones are kept by the rules in adsFeedback.ts. Every kept ad is still reviewed by a person. */
  autoPick: z.boolean().default(false),
  /** An idea needs this score (3 to 5) to count as a strong pick. */
  autoPickMinScore: z.number().int().min(3).max(5).default(4),
  /** What each product's ads are for. The weekly ideas and the alerts are read against it. */
  goalSonicHire: adGoalSchema.default("demo_bookings"),
  goalRaya: adGoalSchema.default("early_access"),
  /** The most the ads should spend in one day, in USD. A limit we show and judge by; the ad platforms enforce the real budget. */
  dailyCapUsd: z.number().min(1).max(100_000).default(35),
  /** The cost per sign-up we aim for. Empty until a manager knows it. */
  targetCpaUsd: z.number().positive().max(1_000_000).nullable().default(null),
  /** When an ad counts as weak or tired. The starting values come from the team's rules and a manager can change them. */
  insightSettings: insightSettingsSchema.default(DEFAULT_INSIGHT_SETTINGS),
});
export type AdSchedule = z.infer<typeof adScheduleSchema>;

export const adScheduleStatusSchema = z.object({
  schedule: adScheduleSchema,
  timezone: z.string(),
  /** The week (Monday date) the last automatic run covered, if any. */
  lastRunWeek: z.string().nullable(),
  lastRunAt: z.string().nullable(),
  lastMessage: z.string().nullable(),
  /** When a manager switched auto-pick on (the sign-off), or null. */
  autoPickAt: z.string().nullable().default(null),
  /** Spend on ads this month, in USD, and whether it has reached the cap. */
  monthSpendUsd: z.number(),
});
export type AdScheduleStatus = z.infer<typeof adScheduleStatusSchema>;
export const adScheduleSaveSchema = z.object({ siteId: z.string().min(1).max(100), schedule: adScheduleSchema });

/** Calendar parts of an instant in an IANA time zone. `weekday` is 1 = Monday … 7 = Sunday. */
export function localParts(now: Date, timeZone: string): { date: string; weekday: number; minutes: number } {
  let tz = timeZone;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
  } catch {
    tz = "UTC";
  }
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23", weekday: "short" })
      .formatToParts(now)
      .map((p) => [p.type, p.value])
  );
  const weekday = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(String(parts.weekday)) + 1;
  return { date: `${parts.year}-${parts.month}-${parts.day}`, weekday, minutes: Number(parts.hour) * 60 + Number(parts.minute) };
}

/**
 * True once per week: the scheduled weekday has arrived, the scheduled time has passed that day (a missed minute still
 * runs later the same day), and this week has not run yet.
 */
export function isRunDue(s: AdSchedule, timeZone: string, now: Date, lastRunWeek: string | null): boolean {
  if (!s.enabled || (!s.sonicHire && !s.raya)) return false;
  const l = localParts(now, timeZone);
  if (l.weekday !== s.weekday || l.minutes < s.hour * 60 + s.minute) return false;
  return lastRunWeek !== weekStartOf(l.date);
}

/** The week key the run is recorded under (the Monday of the local week). */
export function runWeekKey(timeZone: string, now: Date): string {
  return weekStartOf(localParts(now, timeZone).date);
}
